import { useEffect, useRef, useState, type FC } from "react";
import {
  type AnnouncementPoliteness,
  subscribeToAnnouncements,
} from "@/hooks/useAnnounce";

const ANNOUNCEMENT_DELAY_MS = 50;
const ANNOUNCEMENT_DISPLAY_MS = 1000;

const LiveAnnouncer: FC = () => {
  const [politeMessage, setPoliteMessage] = useState("");
  const [assertiveMessage, setAssertiveMessage] = useState("");
  const timeoutRefs = useRef<
    Record<AnnouncementPoliteness, ReturnType<typeof setTimeout> | null>
  >({
    polite: null,
    assertive: null,
  });
  const queueRefs = useRef<Record<AnnouncementPoliteness, string[]>>({
    polite: [],
    assertive: [],
  });

  useEffect(() => {
    const setMessage = (
      politeness: AnnouncementPoliteness,
      message: string,
    ) => {
      if (politeness === "assertive") {
        setAssertiveMessage(message);
        return;
      }

      setPoliteMessage(message);
    };

    const clearRegionTimeout = (politeness: AnnouncementPoliteness) => {
      const timeout = timeoutRefs.current[politeness];

      if (timeout) {
        clearTimeout(timeout);
        timeoutRefs.current[politeness] = null;
      }
    };

    const scheduleNextAnnouncement = (politeness: AnnouncementPoliteness) => {
      if (
        timeoutRefs.current[politeness] !== null ||
        queueRefs.current[politeness].length === 0
      ) {
        return;
      }

      setMessage(politeness, "");
      timeoutRefs.current[politeness] = setTimeout(() => {
        const message = queueRefs.current[politeness].shift();

        if (message === undefined) {
          timeoutRefs.current[politeness] = null;
          return;
        }

        setMessage(politeness, message);
        timeoutRefs.current[politeness] = setTimeout(() => {
          timeoutRefs.current[politeness] = null;
          scheduleNextAnnouncement(politeness);
        }, ANNOUNCEMENT_DISPLAY_MS);
      }, ANNOUNCEMENT_DELAY_MS);
    };

    const unsubscribe = subscribeToAnnouncements((message, politeness) => {
      queueRefs.current[politeness].push(message);
      scheduleNextAnnouncement(politeness);
    });

    return () => {
      unsubscribe();
      clearRegionTimeout("polite");
      clearRegionTimeout("assertive");
      queueRefs.current.polite = [];
      queueRefs.current.assertive = [];
    };
  }, []);

  return (
    <div className="sr-only">
      <div aria-live="polite" aria-atomic="true">
        {politeMessage}
      </div>
      <div aria-live="assertive" aria-atomic="true">
        {assertiveMessage}
      </div>
    </div>
  );
};

export default LiveAnnouncer;
